import { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { Button } from '../../../components/ui/button';
import type { Experiment } from '../types';
import { formatDate } from '../utils';
import { ExperimentStatusBadge, RunStatusBadge } from './StatusBadges';

interface ExperimentCardProps {
  experiment: Experiment;
  isRunningPersonalization: boolean;
  onApprove: (experiment: Experiment) => void;
  onGeneratePersonalization: (
    experiment: Experiment,
    audienceMode: 'limited' | 'all',
    userLimit?: number,
  ) => void;
}

export function ExperimentCard({
  experiment,
  isRunningPersonalization,
  onApprove,
  onGeneratePersonalization,
}: ExperimentCardProps) {
  const [audienceMode, setAudienceMode] = useState<'limited' | 'all'>(
    'limited',
  );
  const [userLimit, setUserLimit] = useState('100');

  const latestRun = experiment.latestRun;
  const canGenerate =
    experiment.status === 'approved' || experiment.status === 'running';

  const handleGenerate = () => {
    const parsed = Number(userLimit);
    if (
      audienceMode === 'limited' &&
      (!Number.isInteger(parsed) || parsed <= 0)
    ) {
      toast.error('Limited users requires a positive user limit');
      return;
    }
    onGeneratePersonalization(
      experiment,
      audienceMode,
      audienceMode === 'limited' ? parsed : undefined,
    );
  };

  return (
    <div className="rounded-xl border border-neutral-800 bg-neutral-900/50 p-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-white">
            {experiment.experimentId}
          </div>
          <div className="mt-0.5 text-xs text-neutral-500">
            {experiment.weekId} · {experiment.whitelistAndroidPackages.length}{' '}
            apps
          </div>
        </div>
        <ExperimentStatusBadge status={experiment.status} />
      </div>

      {latestRun && (
        <div className="mt-3 rounded-lg border border-neutral-800 bg-neutral-950/40 p-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-xs font-medium text-neutral-300">
              Latest run
            </span>
            <div className="flex items-center gap-2">
              <RunStatusBadge status={latestRun.status} />
              <Link
                to={`/app-spotlight/runs/${latestRun.id}`}
                className="rounded-md border border-neutral-700 bg-neutral-900 px-2 py-1 text-[10px] font-medium uppercase tracking-wide text-neutral-200 hover:bg-neutral-800 hover:text-white"
              >
                View details
              </Link>
            </div>
          </div>
          <div className="mt-3 grid gap-2 text-xs text-neutral-400 sm:grid-cols-2">
            <div>
              Audience:{' '}
              <span className="text-neutral-200">
                {latestRun.audienceMode}
                {latestRun.requestedUserLimit
                  ? ` (${latestRun.requestedUserLimit})`
                  : ''}
              </span>
            </div>
            <div>
              Output:{' '}
              <span className="text-neutral-200">
                {latestRun.outputUserCount ?? 'pending'}
              </span>
            </div>
            <div>
              Eligible:{' '}
              <span className="text-neutral-200">
                {latestRun.eligibleUserCount ?? 'pending'}
              </span>
            </div>
            <div>
              Updated:{' '}
              <span className="text-neutral-200">
                {formatDate(latestRun.lastHeartbeatAt ?? latestRun.completedAt)}
              </span>
            </div>
            {latestRun.errorMessage && (
              <div className="text-red-300 sm:col-span-2">
                {latestRun.errorMessage}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={experiment.status !== 'draft'}
          onClick={() => onApprove(experiment)}
        >
          Approve
        </Button>
        <select
          aria-label="Personalization audience"
          value={audienceMode}
          onChange={(event) =>
            setAudienceMode(event.target.value as 'limited' | 'all')
          }
          className="h-8 rounded-lg border border-neutral-700 bg-neutral-900 px-3 text-xs text-white focus:border-teal-500/60 focus:outline-none"
        >
          <option value="limited">Limited users</option>
          <option value="all">All users</option>
        </select>
        {audienceMode === 'limited' && (
          <input
            aria-label="Personalization user limit"
            value={userLimit}
            onChange={(event) => setUserLimit(event.target.value)}
            inputMode="numeric"
            className="h-8 w-20 rounded-lg border border-neutral-700 bg-neutral-900 px-3 text-xs text-white focus:border-teal-500/60 focus:outline-none"
          />
        )}
        <Button
          variant="outline"
          size="sm"
          disabled={isRunningPersonalization || !canGenerate}
          onClick={handleGenerate}
        >
          Generate
        </Button>
      </div>
    </div>
  );
}
